<script lang="ts">
import { defineComponent } from 'vue';
import { useProfileStore, type UserBetSlip } from '@/stores/profile';
import { mapActions, mapState } from 'pinia';
import Headline from '@/components/headline.vue';
import { generateDates } from '../../../../libs/helpers/dates';
import Decimal from 'decimal.js-light';
import { formatDate, formatMoney } from '@/utils/format';

export default defineComponent({
  name: 'profile',
  components: { Headline },
  computed: {
    ...mapState(useProfileStore, ['loading', 'profile', 'profileId']),
    id() {
      return parseInt(this.$route.params.id as string);
    },
    profileTimed() {
      if (!this.profile) return [];

      let startDate = null;
      let endDate = null;

      for (const record of this.profile) {
        const [date] = record.createdAt.split('T');

        if (!startDate || startDate > date) startDate = date;
        if (!endDate || endDate < date) endDate = date;
      }

      const dates = generateDates<UserBetSlip>(startDate!, endDate!, 7);

      for (const record of this.profile) {
        const [date] = record.createdAt.split('T');

        const stake = record.vendorStakeAmount;
        const payout = new Decimal(record.payout || 0)
          .div(record.stakeAmount)
          .mul(record.vendorStakeAmount);
        const refund = new Decimal(record.refund || 0)
          .div(record.stakeAmount)
          .mul(record.vendorStakeAmount);

        dates[date].records.push(record);
        dates[date].stake = dates[date].stake.add(stake);
        dates[date].gain = dates[date].gain.add(payout).add(refund);
      }

      return dates;
    },
    maxBets() {
      if (!this.profileTimed) return 0;

      return Object.values(this.profileTimed)
        .map((r) => r.records)
        .reduce((max, c) => Math.max(max, c.length), 0);
    },
    maxAmount() {
      return Object.values(this.profileTimed).reduce(
        (max, c) => Math.max(max, c.stake.toNumber(), c.gain.toNumber()),
        0
      );
    },
    totalStake() {
      return (
        this.profile
          ?.reduce((acc, rec) => acc.plus(rec.vendorStakeAmount), new Decimal(0))
          .toFixed(4) || 0
      );
    },
    totalWin() {
      return (
        this.profile
          ?.reduce((acc, rec) => {
            const payout = new Decimal(rec.payout || 0);
            const totalPayout = payout.plus(rec.refund || 0);

            return acc.plus(totalPayout.div(rec.stakeAmount).mul(rec.vendorStakeAmount));
          }, new Decimal(0))
          .toFixed(4) || 0
      );
    },
    totalCharges() {
      let balance = new Decimal(0);
      let charges = new Decimal(0);
      if (!this.profile) return balance;

      for (let i = 0; i < this.profile.length; i++) {
        const slip = this.profile[i];
        const payout = new Decimal(slip.payout || 0).plus(slip.refund || 0).toNumber();
        const vendorAmount = slip.vendorStakeAmount;
        const vendorPayout = new Decimal(payout || 0)
          .mul(slip.vendorStakeAmount)
          .div(slip.stakeAmount)
          .toNumber();

        balance = balance.minus(vendorAmount);

        if (balance.lessThan(0)) {
          charges = charges.minus(balance);
          balance = new Decimal(0);
        }

        balance = balance.plus(vendorPayout);
      }

      return charges.toFixed(2);
    },
    income() {
      return new Decimal(this.totalWin).sub(this.totalStake).toFixed(2);
    },
    totalYield() {
      return new Decimal(this.income).div(this.totalStake).mul(100).toFixed(2);
    },
    totalROI() {
      return new Decimal(this.income).div(this.totalCharges).mul(100).sub(100).toFixed(2);
    },
  },
  data() {
    return {
      textId: null,
    };
  },
  methods: {
    formatMoney,
    formatDate,
    ...mapActions(useProfileStore, ['fetchProfile']),
    onKeyUp() {
      if (this.textId && Number.isInteger(+this.textId)) {
        this.$router.push({ name: 'profile', params: { id: this.textId } });
      }
    },
    displayDate(record: string) {
      return this.profileTimed ? formatDate(record, 'DD MMMM') : null;
    },
    dayShift(date: string) {
      const time = date.split('T')[1];
      const timePart: string[] = time.split(':');
      const shift = parseInt(timePart[0]) * 60 + parseInt(timePart[1]);

      return +shift / (60 * 24);
    },
  },
  mounted() {
    if (this.profileId && !this.id) {
      this.$router.replace({ name: 'profile', params: { id: this.profileId } });
    }
    if ((this.id && !this.profile) || (this.id && this.profileId !== this.id)) {
      this.fetchProfile(+this.id);
    }
  },
  watch: {
    '$route.params.id'(val) {
      if (val) {
        this.fetchProfile(+this.$route.params.id);
      }
    },
  },
});
</script>

<template>
  <template v-if="loading">
    <div class="relative z-10">
      <div
        style="height: 100vh; position: absolute; left: 0; right: 0"
        class="place-content-center text-center w-full bg-opacity-20 bg-black"
      >
        <i class="pi pi-spinner animate-spin text-gray-200" style="font-size: 5em" />
      </div>
    </div>
  </template>
  <template v-else-if="!profile && !id">
    <div class="text-center p-2">
      "Позови меня тихо по имени..."<br />
      <InputText
        v-model="textId"
        class="m-2 p-2 text-center"
        placeholder="ID пользователя"
        @keyup.enter="onKeyUp"
      />
    </div>
  </template>
  <template v-else>
    <teleport to="#title">
      <headline class="mx-4 my-2">
        Профиль игрока
        <span class="text-white">{{ id }}</span>
      </headline>
    </teleport>

    <div class="ml-4 my-4 inline-grid grid-cols-4 gap-2" v-if="profile && profile.length">
      <div>Всего ставок:</div>
      <div>{{ profile.length }}</div>

      <div>Первая ставка:</div>
      <div>{{ formatDate(profile[0].createdAt, 'DD.MM.YYYY') }}</div>

      <div>Среднее ставок в день:</div>
      <div>{{ (profile.length / Object.keys(profileTimed).length).toFixed(2) }}</div>

      <div>Последняя ставка:</div>
      <div>{{ formatDate(profile[profile.length - 1].createdAt, 'DD.MM.YYYY') }}</div>

      <div>Сумма ставок:</div>
      <div>{{ totalStake }}</div>

      <div>Сумма выигрышей:</div>
      <div>{{ totalWin }}</div>

      <div>Собственных затрат:</div>
      <div>{{ totalCharges }}</div>

      <div>Чистая прибыль:</div>
      <div>{{ income }}</div>

      <div>Yield:</div>
      <div
        :class="{
          'text-red-500': parseFloat(totalYield) < 0,
          'text-green-500': parseFloat(totalYield) > 0,
        }"
      >
        {{ totalYield }}%
      </div>

      <div>ROI</div>
      <div
        :class="{
          'text-red-500': parseFloat(totalROI) < 0,
          'text-green-500': parseFloat(totalROI) > 0,
        }"
      >
        {{ totalROI }}%</div>
    </div>

    <div class="relative border-gray-800 bg-gray-800 h-[252px] overflow-x-scroll mx-4 my-2">
      <template v-for="[date, betSlips] of Object.entries(profileTimed)">
        <div
          :class="{
            absolute: true,
            group: true,
            'w-[22px]': true,
            'hover:bg-gray-600': true,
            'border border-dashed border-gray-700 border-r-0 border-t-0 border-b-0': true,
            'text-center text-xs': true,
            'text-gray-700 hover:text-white': true,
          }"
          :style="`
            left: ${betSlips.i * 24}px;
            top: 0;
            height: 250px;
          `"
        >
          <div
            class="relative z-10 overflow-visible h-0 left-full w-[150px]"
            v-if="betSlips.records.length"
          >
            <div
              class="hidden group-hover:grid bg-gray-800 border rounded w-[150px] p-2 text-left grid-cols-[3fr_2fr]"
              style="font-size: 0.7em"
            >
              <span> Ставок </span>
              <span class="text-right"> {{ betSlips.records.length }} </span>
              <span> Сумма </span>
              <span class="text-right"> {{ betSlips.stake.toFixed(2) }} </span>
              <span> Выигрыш </span>
              <span class="text-right"> {{ betSlips.gain.toFixed(2) }} </span>
              <span> Чистый выигрыш </span>
              <span class="text-right">
                {{ betSlips.gain.sub(betSlips.stake).toFixed(2) }}
              </span>

              <span class="mt-3">00:00</span>
              <span class="mt-3 text-right">23:59</span>

              <div class="w-[130px] h-[2em] col-span-2 bg-gray-600 relative">
                <div
                  v-for="r of betSlips.records"
                  class="absolute 3px bg-green-500 w-[1px] h-[2em]"
                  :style="`
                    left: ${dayShift(r.createdAt) * 130}px
                  `"
                ></div>
              </div>
            </div>
          </div>
          <div class="h-[20px] overflow-hidden">
            {{ betSlips.records.length || '&nbsp;' }}
          </div>
          <div
            class="h-[230px] w-[22px] p-0.5 rotate-180 text-left"
            style="line-height: 22px; writing-mode: vertical-lr"
          >
            {{ displayDate(date) }}
          </div>
        </div>

        <div
          :class="{
            'absolute bg-blue-500 border-b-2': true,
            'w-[22px] pointer-events-none': true,
            'border-white': betSlips.records.length,
            'border-gray-500': !betSlips.records.length,
          }"
          :style="`
            left: ${betSlips.i * 24}px;
            bottom: 150px;
            height: ${(betSlips.records.length / maxBets) * 80}px;
            border-bottom-width: 1px;
          `"
        />

        <div
          :class="{
            'absolute bg-green-500': true,
            'w-[11px] pointer-events-none': true,
          }"
          :style="`
            left: ${betSlips.i * 24}px;
            top: 100px;
            height: ${(betSlips.stake.toNumber() / maxAmount) * 80}px;
          `"
        />

        <div
          :class="{
            'absolute bg-red-500': true,
            'w-[11px] pointer-events-none': true,
          }"
          :style="`
            left: ${betSlips.i * 24 + 11}px;
            top: 100px;
            height: ${(betSlips.gain.toNumber() / maxAmount) * 80}px;
          `"
        />
      </template>
    </div>
    <div class="mx-4 text-xs text-gray-400 text-right">
      <div class="h-[.6em] w-[.6em] inline-block bg-blue-500 mx-2"></div>
      <div class="inline-block mr-6">количество ставок за день</div>

      <div class="h-[.6em] w-[.6em] inline-block bg-green-500 mx-2"></div>
      <div class="inline-block mr-6">сумма ставок за день</div>

      <div class="h-[.6em] w-[.6em] inline-block bg-red-500 mx-2"></div>
      <div class="inline-block mr-6">сумма выигрышей за день</div>
    </div>

    <!--    <template v-for="record of Object.keys(profileTimed)">-->
    <!--      <div>-->
    <!--        {{ profileTimed[record].i }}-->
    <!--        -&#45;&#45;-->
    <!--        {{ record }}-->
    <!--        -&#45;&#45;-->
    <!--        {{ profileTimed[record].records.length }}-->
    <!--        -&#45;&#45;-->
    <!--        {{ profileTimed[record].stake.toFixed(4) }}-->
    <!--        -&#45;&#45;-->
    <!--        {{ profileTimed[record].gain.toFixed(4) }}-->
    <!--      </div>-->
    <!--    </template>-->
  </template>
</template>

<style scoped lang="scss"></style>
